import { useState } from "react";
import { CouponProps } from "../../store/actionType";
import { CustomTooltip } from "components";
import { copyToClipboard } from "utils/helpers/CopyToClipboard";
import { CopyIcon, LensIcon } from "assets";

interface Props {
  couponData: CouponProps[];
}

const CouponCards: React.FC<Props> = ({ couponData }) => {
  const [search, setSearch] = useState("");
  const [copiedMap, setCopiedMap] = useState<{ [key: string]: boolean }>({});
  const [copiedAll, setCopiedAll] = useState(false);
  const [totalCouponsCopied, setTotalCouponsCopied] = useState(0);
  const filteredCoupons = couponData.filter(
    (item: CouponProps) =>
      !item.redeemed &&
      (item.code?.toLowerCase().includes(search.toLowerCase()) ||
        item.id?.toLowerCase().includes(search.toLowerCase()) ||
        item.membership_duration_id
          ?.toLowerCase()
          .includes(search.toLowerCase()))
  );
  const handleCopy = async (id: string, code: string) => {
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedMap((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setCopiedMap((prev) => ({ ...prev, [id]: false }));
      }, 1500);
    }
  };
  const handleCopyAll = async () => {
    const source = search.trim() ? filteredCoupons : couponData;
    const couponCodes = source.map((item) => item.code).join("\n");
    if (!couponCodes) return;
    const success = await copyToClipboard(couponCodes);
    if (success) {
      setTotalCouponsCopied(source.length);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    }
  };
  const formatIndex = (num: number) => {
    const str = num.toString();
    if (str.length <= 6) return str;
    return `${str[0]}*${str[str.length - 1]}`;
  };

  return (
    <div className="m-4">
      <div className="mb-4 w-2/5 relative flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by Duration/Coupon ID or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-50 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 pl-8"
          />
          <span className="absolute top-1/2 left-3 -translate-y-1/2">
            <LensIcon />
          </span>
        </div>
        <div className="border border-primary-500 rounded-md hover:bg-primary-500 hover:text-white">
          <CustomTooltip
            title={`${
              copiedAll
                ? `${totalCouponsCopied} Coupons Copied!`
                : "Copy all visible coupons"
            } `}
            placement="right"
          >
            <button
              onClick={handleCopyAll}
              className="px-3 py-2 border   flex items-center gap-1"
            >
              <CopyIcon className="size-4" />
              <span className="text-sm">Copy All</span>
            </button>
          </CustomTooltip>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-0">
        {filteredCoupons.length > 0 ? (
          filteredCoupons.map((item: CouponProps, index: number) => (
            <div
              key={item.id}
              className="relative bg-white border border-dashed border-gray-300 
                     flex flex-col justify-between
                     rounded-lg my-2 mx-1 overflow-hidden"
            >
              <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full border border-gray-300"></span>
              <span className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-50 rounded-full border border-gray-300"></span>

              <div className="p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg tracking-wide">
                    {item.code ? "*******" :""}
                  </span>
                  <CustomTooltip
                    title={copiedMap[item.id] ? "Copied!" : "Copy coupon code"}
                    placement="top"
                  >
                    <button
                      onClick={() => handleCopy(item.id, item.code)}
                      className="p-1 rounded hover:bg-gray-100"
                    >
                      <CopyIcon className="size-4" />
                    </button>
                  </CustomTooltip>
                </div>
                <p className="text-sm text-gray-600">
                  Redeemed:{" "}
                  <span
                    className={
                      item.redeemed ? "text-red-500" : "text-green-600"
                    }
                  >
                    {item.redeemed ? true : false}
                  </span>
                </p>
              </div>
              <div className="bg-gray-100 text-gray-600 text-xs text-center py-1 border-t border-dashed">
                #{formatIndex(index + 1)}
              </div>
            </div>
          ))
        ) : (
          <p className="col-span-6 text-xl text-center text-gray-500 h-full my-10">
            No coupons found.
          </p>
        )}
      </div>
    </div>
  );
};

export default CouponCards;
